import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../../api';

const { 
  FiX, FiPlay, FiCheck, FiChevronRight, FiChevronLeft, 
  FiAward, FiLoader, FiBookOpen, FiXCircle, FiRefreshCw 
} = FiIcons;

const getEmbedUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})/);
  if (ytMatch && ytMatch[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch && vimeoMatch[1]) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch && driveMatch[1]) return `https://drive.google.com/file/d/${driveMatch[1]}/preview?rm=minimal`;
  if (url.includes('app.dadan.io')) return url;
  if (url.includes('embed') || url.endsWith('.mp4')) return url;
  return null;
};

const acknowledgementItems = [
  "I understand that protecting company information is part of my responsibilities at BIS.",
  "I understand how to recognize and report suspicious emails, messages, phone calls, QR codes, and other potential security threats.",
  "I understand that I must immediately report suspected security incidents to security@blessinnovativesolutions.com (or follow the BIS incident reporting process).",
  "I understand that I must never share my passwords, approve unexpected MFA requests, or use another employee's account.",
  "I understand that confidential company information must only be stored and shared using approved BIS systems.",
  "I understand that confidential company information must never be uploaded to unauthorized AI tools or personal cloud storage.",
  "I understand that there is no punishment for reporting an honest mistake, and that timely reporting helps protect the company.",
  "I understand that I am responsible for following BIS security policies and procedures while performing my job."
];

const ModulePlayerModal = ({ campaign, module, employeeId, employee, assignment, onClose, onComplete }) => {
  const [mode, setMode] = useState('content'); // 'content', 'quiz', 'policy-acknowledgement', 'acknowledgement', 'result'
  const [questions, setQuestions] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [score, setScore] = useState(assignment?.score || 0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [pendingScore, setPendingScore] = useState(null);
  const [policyAckChecked, setPolicyAckChecked] = useState({});
  const [ackChecked, setAckChecked] = useState({});
  
  const sessionStartTime = useRef(Date.now());

  useEffect(() => {
    Promise.all([
      dataFetch(`read/quiz_questions?module_id=${module.id}`),
      dataFetch('read/policies')
    ]).then(([quizRes, polRes]) => {
      setQuestions(quizRes?.data || []);
      
      const allPolicies = polRes?.data || [];
      const linkedPolicies = allPolicies.filter(p => {
        if (!p.module_ids) return false;
        const ids = p.module_ids.split(',').map(s => s.trim());
        return ids.includes(String(module.id));
      });
      setPolicies(linkedPolicies);
      
      setLoading(false);
    });
  }, [module.id]);

  const handleCompleteContent = async () => {
    if (questions.length > 0) {
      setMode('quiz');
    } else {
      setPendingScore(100);
      if (policies.length > 0) {
        setMode('policy-acknowledgement');
      } else {
        setMode('acknowledgement');
      }
    }
  };

  const submitScore = async (finalScore) => {
    setSubmitting(true);
    const userId = localStorage.getItem('user_id');
    const newAttempts = (assignment?.attempts || 0) + 1;
    const sessionTime = Math.floor((Date.now() - sessionStartTime.current) / 1000);
    const newTimeSpent = (assignment?.time_spent || 0) + sessionTime;
    const currentYear = new Date().getFullYear();

    const payload = {
      campaign_id: String(campaign.id),
      employee_id: String(employeeId),
      module_id: String(module.id),
      score: Math.round(finalScore),
      status: 'Completed',
      user_id: userId,
      attempts: newAttempts,
      time_spent: newTimeSpent,
      year: currentYear
    };

    let newAssignment;
    if (assignment) {
      await dataFetch(`update/assignments/${assignment.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      newAssignment = { ...assignment, ...payload };
    } else {
      const res = await dataFetch('create/assignments', { method: 'POST', body: JSON.stringify(payload) });
      newAssignment = { ...payload, id: res?.data?.id };
    }
    
    setScore(Math.round(finalScore));
    setMode('result');
    setSubmitting(false);
    sessionStartTime.current = Date.now(); // Reset timer in case they retake
    onComplete(newAssignment);
  };

  const handleQuizSubmit = () => {
    let correct = 0;
    questions.forEach((q, i) => {
      if (answers[i] === q.answer) correct++;
    });
    const pct = (correct / questions.length) * 100;
    
    if (pct >= 80) {
      setPendingScore(pct);
      if (policies.length > 0) {
        setMode('policy-acknowledgement');
      } else {
        setMode('acknowledgement');
      }
    } else {
      submitScore(pct);
    }
  };

  const handlePolicyAckSubmit = async () => {
    setSubmitting(true);
    const userId = localStorage.getItem('user_id');
    
    // Format safely to YYYY-MM-DD HH:mm:ss for SQL Database
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');

    try {
      const promises = policies.map(policy => {
        if (policyAckChecked[policy.id]) {
          return dataFetch('create/policy_acknowledgements', {
            method: 'POST',
            body: JSON.stringify({
              employee_id: String(employeeId),
              policy_name: policy.policy_name,
              policy_version: policy.version || '1.0',
              policy_id: policy.id,
              date_time: now,
              status: 'Accepted',
              user_id: userId
            })
          });
        }
        return Promise.resolve();
      });
      
      await Promise.all(promises);
    } catch (e) {
      console.error("Failed to save policy acknowledgements", e);
    }
    
    setSubmitting(false);
    setMode('acknowledgement');
  };

  const handleAcknowledgementSubmit = async () => {
    setSubmitting(true);
    
    const userId = localStorage.getItem('user_id');
    // Format safely to YYYY-MM-DD HH:mm:ss for SQL Database
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    
    try {
      await dataFetch('create/module_acknowledgements', {
        method: 'POST',
        body: JSON.stringify({
          employee_name: `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || 'Unknown Learner',
          employee_email: employee?.email || '',
          employee_id: String(employeeId),
          module_id: String(module.id),
          campaign_id: String(campaign.id),
          date_time: now,
          academy_version: '1.0',
          policy_version: '1.0',
          status: 'Accepted',
          user_id: userId
        })
      });
    } catch (e) {
      console.error("Failed to save acknowledgement", e);
    }
    
    await submitScore(pendingScore);
  };

  const handleRetake = () => {
    setAnswers({});
    setCurrentQuestionIndex(0);
    setScore(0);
    setMode('quiz');
  };

  const handleClose = async () => {
    const sessionTime = Math.floor((Date.now() - sessionStartTime.current) / 1000);
    const currentYear = new Date().getFullYear();
    // If they haven't finished (mode !== 'result') and spent more than 5 seconds, save the time
    if (mode !== 'result' && sessionTime > 5) {
      if (assignment?.id) {
        const payload = {
          campaign_id: String(campaign.id),
          employee_id: String(employeeId),
          module_id: String(module.id),
          score: assignment.score || 0,
          status: assignment.status || 'In Progress',
          user_id: assignment.user_id || localStorage.getItem('user_id'),
          attempts: assignment.attempts || 0,
          time_spent: (assignment.time_spent || 0) + sessionTime,
          year: currentYear
        };
        await dataFetch(`update/assignments/${assignment.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        const userId = localStorage.getItem('user_id');
        await dataFetch('create/assignments', {
          method: 'POST',
          body: JSON.stringify({
            campaign_id: String(campaign.id),
            employee_id: String(employeeId),
            module_id: String(module.id),
            status: 'In Progress',
            user_id: userId,
            time_spent: sessionTime,
            attempts: 0,
            year: currentYear
          })
        });
      }
    }
    onClose();
  };

  const embedUrl = module.video_url ? getEmbedUrl(module.video_url) : null;
  const isGoogleDrive = embedUrl?.includes('drive.google.com');

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          exit={{ opacity: 0, y: 20 }}
          className="absolute inset-0 bg-white flex flex-col z-10 overflow-hidden"
        >
          
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 shadow-sm sticky top-0 z-20">
            <div>
              <h2 className="text-xl font-bold text-slate-800">{module.title}</h2>
              <p className="text-sm text-slate-500 mt-0.5">{campaign.campaignName}</p>
            </div>
            <button 
              onClick={handleClose} 
              className="text-slate-500 hover:text-slate-800 p-2.5 rounded-full hover:bg-slate-200 transition-colors bg-white border border-slate-200 shadow-sm"
            >
              <SafeIcon icon={FiX} className="text-2xl" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 md:p-12 lg:px-32 xl:px-64 pb-32">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-32 text-slate-400">
                <SafeIcon icon={FiLoader} className="animate-spin text-5xl mb-4 text-blue-500" />
                <p className="text-lg">Loading module content...</p>
              </div>
            ) : mode === 'content' ? (
              <div className="space-y-8 max-w-5xl mx-auto">
                {(module.deliveryFormat === 'Video' && module.video_url) ? (
                  <div className="w-full aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl border border-slate-200 relative">
                    {embedUrl ? (
                      isGoogleDrive ? (
                        <iframe 
                          src={embedUrl} 
                          className="absolute left-0 w-full border-0" 
                          style={{ top: '-60px', height: 'calc(100% + 60px)' }}
                          allowFullScreen 
                          title="Module Video" 
                        />
                      ) : (
                        <iframe src={embedUrl} className="absolute top-0 left-0 w-full h-full border-0" allowFullScreen title="Module Video" />
                      )
                    ) : (
                      <div className="flex flex-col items-center justify-center w-full h-full text-slate-400 absolute inset-0">
                        <SafeIcon icon={FiPlay} className="text-5xl mb-3" />
                        <p className="text-lg">Video preview not available</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-blue-50 text-blue-800 p-8 rounded-2xl border border-blue-100 shadow-sm">
                    <SafeIcon icon={FiBookOpen} className="text-5xl text-blue-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-4">Reading Material</h3>
                    <div className="prose prose-blue max-w-none">
                      <p className="text-base leading-relaxed whitespace-pre-wrap">{module.description}</p>
                    </div>
                  </div>
                )}
                
                <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200 shadow-sm">
                  <h4 className="text-xl font-bold text-slate-800 mb-3">Module Details</h4>
                  <p className="text-base text-slate-600 leading-relaxed whitespace-pre-wrap">{module.description}</p>
                </div>
              </div>
            ) : mode === 'quiz' ? (
              <div className="max-w-3xl mx-auto pb-32">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-3xl font-bold text-slate-800">Knowledge Check</h3>
                  <span className="text-sm font-bold bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                </div>

                <div className="w-full bg-slate-100 h-2.5 rounded-full mb-10 overflow-hidden">
                  <div 
                    className="bg-blue-500 h-full transition-all duration-500 ease-out"
                    style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                  />
                </div>

                {(() => {
                  const q = questions[currentQuestionIndex];
                  if (!q) return null;
                  let opts = [];
                  let expls = [];
                  try { opts = JSON.parse(q.options || '[]'); } catch { opts = []; }
                  try { expls = JSON.parse(q.explanations || '[]'); } catch { expls = []; }
                  
                  const selectedAns = answers[currentQuestionIndex];
                  const isAnswered = !!selectedAns;
                  const selectedIndex = opts.findIndex(o => o === selectedAns);
                  const selectedExplanation = selectedIndex !== -1 ? expls[selectedIndex] : null;

                  return (
                    <motion.div 
                      key={currentQuestionIndex}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white border-2 border-slate-100 rounded-2xl p-8 shadow-sm"
                    >
                      <p className="font-bold text-slate-800 mb-8 text-xl leading-relaxed">
                        {q.question}
                      </p>
                      
                      <div className="space-y-4">
                        {opts.filter(o => o.trim()).map((opt, oIndex) => {
                          let borderClass = 'border-slate-200 hover:border-blue-300 hover:bg-slate-50';
                          let bgClass = 'bg-white';
                          let textClass = 'text-slate-700';
                          let Icon = null;
                          let iconColor = '';

                          if (isAnswered) {
                            if (opt === q.answer) {
                              borderClass = 'border-emerald-500';
                              bgClass = 'bg-emerald-50';
                              textClass = 'text-emerald-800 font-bold';
                              Icon = FiCheck;
                              iconColor = 'text-emerald-600';
                            } else if (opt === selectedAns) {
                              borderClass = 'border-red-500';
                              bgClass = 'bg-red-50';
                              textClass = 'text-red-800 font-bold';
                              Icon = FiXCircle;
                              iconColor = 'text-red-600';
                            } else {
                              borderClass = 'border-slate-100 opacity-50';
                              bgClass = 'bg-slate-50';
                              textClass = 'text-slate-400';
                            }
                          }

                          return (
                            <button 
                              key={oIndex}
                              disabled={isAnswered}
                              onClick={() => {
                                if (!isAnswered) {
                                  setAnswers(prev => ({ ...prev, [currentQuestionIndex]: opt }));
                                }
                              }}
                              className={`w-full flex items-center justify-between text-left p-5 rounded-xl border-2 transition-all ${borderClass} ${bgClass} ${!isAnswered ? 'cursor-pointer' : 'cursor-default'}`}
                            >
                              <span className={`text-lg ${textClass}`}>{opt}</span>
                              {Icon && <SafeIcon icon={Icon} className={`text-2xl ${iconColor}`} />}
                            </button>
                          );
                        })}
                      </div>

                      {isAnswered && (
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`mt-6 p-5 rounded-xl border flex items-start gap-4 ${
                            selectedAns === q.answer 
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
                              : 'bg-red-50 border-red-200 text-red-800'
                          }`}
                        >
                          <div className={`p-2 rounded-full flex-shrink-0 ${
                            selectedAns === q.answer ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'
                          }`}>
                            <SafeIcon icon={selectedAns === q.answer ? FiCheck : FiXCircle} className="text-xl" />
                          </div>
                          <div>
                            <h4 className="font-bold text-lg mb-1">
                              {selectedAns === q.answer ? 'Correct!' : 'Incorrect'}
                            </h4>
                            <p className="text-sm opacity-90 leading-relaxed">
                              {selectedAns === q.answer 
                                ? 'Great job! You selected the right answer.' 
                                : <>The correct answer is <span className="font-bold bg-white/50 px-2 py-0.5 rounded mx-1">{q.answer}</span>.</>
                              }
                            </p>
                            
                            {selectedExplanation && (
                              <p className="text-sm opacity-90 mt-2 pt-2 border-t border-black/10">
                                {selectedExplanation}
                              </p>
                            )}
                            
                            {!selectedExplanation && q.explanation && (
                              <p className="text-sm opacity-90 mt-2 pt-2 border-t border-black/10">
                                {q.explanation}
                              </p>
                            )}
                          </div>
                        </motion.div>
                      )}

                    </motion.div>
                  );
                })()}

                <div className="flex justify-between items-center mt-10">
                  <button 
                    onClick={() => setCurrentQuestionIndex(i => Math.max(0, i - 1))}
                    disabled={currentQuestionIndex === 0}
                    className={`flex items-center gap-2 px-6 py-3 font-bold rounded-xl transition-colors ${currentQuestionIndex === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    <SafeIcon icon={FiChevronLeft} className="text-xl" /> Previous
                  </button>

                  {currentQuestionIndex < questions.length - 1 ? (
                    <button 
                      onClick={() => setCurrentQuestionIndex(i => Math.min(questions.length - 1, i + 1))}
                      disabled={!answers[currentQuestionIndex]}
                      className={`flex items-center gap-2 px-8 py-3 font-bold rounded-xl transition-all ${!answers[currentQuestionIndex] ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                    >
                      Next <SafeIcon icon={FiChevronRight} className="text-xl" />
                    </button>
                  ) : (
                    <button 
                      onClick={handleQuizSubmit} 
                      disabled={!answers[currentQuestionIndex] || submitting}
                      className={`flex items-center gap-2 px-8 py-3 font-bold rounded-xl transition-all ${(!answers[currentQuestionIndex] || submitting) ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5'}`}
                    >
                      {submitting ? <SafeIcon icon={FiLoader} className="animate-spin text-xl" /> : <SafeIcon icon={FiCheck} className="text-xl" />}
                      Finish Quiz
                    </button>
                  )}
                </div>
              </div>
            ) : mode === 'policy-acknowledgement' ? (
              <div className="max-w-4xl mx-auto pb-32">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-3xl font-bold text-slate-800">Policy Acknowledgement</h3>
                </div>
                <div className="bg-white border-2 border-slate-100 rounded-2xl p-8 shadow-sm space-y-6">
                  <p className="text-slate-600 mb-2 text-lg">Please review and acknowledge the following corporate policies related to this training.</p>
                  
                  {policies.map((policy) => (
                    <div key={policy.id} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                       <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                         <h4 className="font-bold text-slate-800">{policy.policy_name} <span className="text-xs font-normal text-slate-500 ml-2">v{policy.version || '1.0'}</span></h4>
                       </div>
                       <div className="p-5 text-slate-700 text-sm whitespace-pre-wrap leading-relaxed max-h-60 overflow-y-auto bg-white">
                         {policy.details || 'No additional details provided.'}
                       </div>
                       <label className={`flex items-start gap-4 p-4 border-t cursor-pointer transition-all ${policyAckChecked[policy.id] ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 hover:bg-slate-100 border-slate-200'}`}>
                          <div className="mt-0.5">
                            <input 
                              type="checkbox" 
                              className="w-5 h-5 text-blue-600 rounded border-slate-300 focus:ring-blue-500 cursor-pointer"
                              checked={!!policyAckChecked[policy.id]}
                              onChange={(e) => setPolicyAckChecked(prev => ({ ...prev, [policy.id]: e.target.checked }))}
                            />
                          </div>
                          <span className={`text-base leading-relaxed ${policyAckChecked[policy.id] ? 'text-blue-900 font-medium' : 'text-slate-700'}`}>
                            I acknowledge that I have read and understand the {policy.policy_name}.
                          </span>
                       </label>
                    </div>
                  ))}
                  
                  <div className="mt-10 pt-6 border-t border-slate-100 flex justify-end">
                    <button 
                      onClick={handlePolicyAckSubmit} 
                      disabled={policies.some(p => !policyAckChecked[p.id]) || submitting}
                      className={`flex items-center gap-2 px-8 py-4 font-bold rounded-xl transition-all text-lg ${(policies.some(p => !policyAckChecked[p.id]) || submitting) ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5'}`}
                    >
                      {submitting ? <SafeIcon icon={FiLoader} className="animate-spin text-xl mr-2" /> : null}
                      Proceed to Security Acknowledgement <SafeIcon icon={FiChevronRight} className="text-xl" />
                    </button>
                  </div>
                </div>
              </div>
            ) : mode === 'acknowledgement' ? (
              <div className="max-w-4xl mx-auto pb-32">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-3xl font-bold text-slate-800">Security Acknowledgement</h3>
                </div>
                <div className="bg-white border-2 border-slate-100 rounded-2xl p-8 shadow-sm space-y-4">
                  <p className="text-slate-600 mb-6 text-lg">Please read and acknowledge the following statements before completing this module.</p>
                  
                  {acknowledgementItems.map((item, idx) => (
                    <label key={idx} className={`flex items-start gap-4 p-5 rounded-xl border-2 cursor-pointer transition-all ${ackChecked[idx] ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'}`}>
                      <div className="mt-0.5">
                        <input 
                          type="checkbox" 
                          className="w-5 h-5 text-blue-600 rounded border-slate-300 focus:ring-blue-500 cursor-pointer"
                          checked={!!ackChecked[idx]}
                          onChange={(e) => setAckChecked(prev => ({ ...prev, [idx]: e.target.checked }))}
                        />
                      </div>
                      <span className={`text-base leading-relaxed ${ackChecked[idx] ? 'text-blue-900 font-medium' : 'text-slate-700'}`}>
                        {item}
                      </span>
                    </label>
                  ))}
                  
                  <div className="mt-10 pt-6 border-t border-slate-100 flex justify-end">
                    <button 
                      onClick={handleAcknowledgementSubmit} 
                      disabled={Object.keys(ackChecked).length !== acknowledgementItems.length || Object.values(ackChecked).some(v => !v) || submitting}
                      className={`flex items-center gap-2 px-8 py-4 font-bold rounded-xl transition-all text-lg ${(Object.keys(ackChecked).length !== acknowledgementItems.length || Object.values(ackChecked).some(v => !v) || submitting) ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5'}`}
                    >
                      {submitting ? <SafeIcon icon={FiLoader} className="animate-spin text-xl" /> : <SafeIcon icon={FiCheck} className="text-xl" />}
                      Submit & Complete Module
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className={`w-40 h-40 rounded-full flex items-center justify-center mb-8 shadow-2xl ${score >= 80 ? 'bg-emerald-100 text-emerald-500' : score >= 50 ? 'bg-amber-100 text-amber-500' : 'bg-red-100 text-red-500'}`}>
                  <SafeIcon icon={FiAward} className="text-7xl" />
                </motion.div>
                {score >= 80 && (
                  <>
                    <h3 className="text-4xl font-bold text-slate-800 mb-4">Congratulations! 🎉</h3>
                    <p className="text-slate-500 mb-10 text-xl">You passed the quiz with a score of <span className="font-bold text-slate-800 text-2xl ml-1">{score}%</span>. Great job demonstrating your understanding of the security awareness concepts covered in this module. Keep applying these best practices to help protect yourself, your colleagues, and BIS from cyber threats.</p>
                  </>
                )}

                {score < 80 && (
                  <>
                    <h3 className="text-4xl font-bold text-slate-800 mb-4">We're sorry, but you did not achieve the required passing score.</h3>
                    <p className="text-slate-500 mb-10 text-xl">You score <span className="font-bold text-slate-800 text-2xl ml-1">{score}%</span>. A minimum score of 8 out of 10 is required to successfully complete this quiz. Please review the training material and try again—you'll have another opportunity to strengthen your knowledge and successfully complete the module.</p>
                  </>
                )}
                
                
                <div className="flex items-center gap-4">
                  {score < 80 && (
                    <button 
                      onClick={handleRetake} 
                      className="flex items-center gap-2 px-8 py-4 text-lg bg-orange-50 border border-orange-200 hover:bg-orange-100 text-orange-700 font-bold rounded-2xl transition-all shadow-sm"
                    >
                      <SafeIcon icon={FiRefreshCw} />
                      Retake Quiz
                    </button>
                  )}
                  <button 
                    onClick={handleClose} 
                    className="px-10 py-4 text-lg bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-2xl transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1"
                  >
                    Return to Dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Fixed Button for Content Mode */}
          {!loading && mode === 'content' && (
            <div className="fixed bottom-8 right-2 z-50">
              <button 
                onClick={handleCompleteContent} 
                className="flex items-center gap-3 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg rounded-2xl transition-all shadow-2xl hover:shadow-[0_20px_40px_-15px_rgba(37,99,235,0.5)] hover:-translate-y-1 border border-blue-500/20"
              >
                {questions.length > 0 ? 'Proceed to Quiz' : 'Mark as Completed'} 
                <SafeIcon icon={FiChevronRight} className="text-2xl" />
              </button>
            </div>
          )}

        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ModulePlayerModal;